
import React, { useState, useRef, useEffect } from "react";
import { Play, Pause, SkipBack, SkipForward } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useToast } from "@/hooks/use-toast";

interface AudioPlayerProps {
  title: string;
  audioSrc?: string;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ title, audioSrc }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const { toast } = useToast();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = new Audio(audioSrc || "/HostCall-ai-welcome-2025-05.mp3");
    audioRef.current = audio;
    
    audio.addEventListener("loadedmetadata", () => {
      setDuration(audio.duration);
    });
    
    audio.addEventListener("timeupdate", () => {
      setCurrentTime(audio.currentTime);
    });
    
    audio.addEventListener("ended", () => {
      setIsPlaying(false);
      setCurrentTime(0);
    });
    
    return () => {
      audio.pause();
      audio.src = "";
      audio.remove();
    };
  }, [audioSrc]);

  const togglePlayPause = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      const playPromise = audioRef.current.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
          })
          .catch(error => {
            console.error("Audio play error:", error);
            toast({
              title: "Audio Playback Issue",
              description: "Click anywhere on the page to enable audio playback."
            });
          });
      }
    }
  };

  const handleSkipForward = () => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.min(audioRef.current.duration, audioRef.current.currentTime + 10);
  };

  const handleSkipBackward = () => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10);
  };

  const handleSliderChange = (value: number[]) => {
    if (!audioRef.current) return;
    const newTime = (value[0] / 100) * duration;
    setCurrentTime(newTime);
    audioRef.current.currentTime = newTime;
  };

  // Format time in MM:SS format
  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  // Calculate slider value as percentage
  const sliderValue = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="w-full px-4 py-6">
      <div className="flex justify-center space-x-6 mb-6">
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-12 w-12"
          onClick={handleSkipBackward}
          aria-label="Skip backward 10 seconds"
        >
          <SkipBack className="h-8 w-8 text-gray-300" />
        </Button>

        <Button
          onClick={togglePlayPause}
          className="rounded-full bg-[#6B47F6] hover:bg-[#5a3bd0] h-16 w-16 flex items-center justify-center"
          aria-label={isPlaying ? "Pause audio" : "Play audio"}
        >
          {isPlaying ? (
            <Pause className="h-8 w-8 text-white" />
          ) : (
            <Play className="h-8 w-8 text-white ml-1" />
          )}
        </Button>

        <Button 
          variant="ghost" 
          size="icon" 
          className="h-12 w-12"
          onClick={handleSkipForward}
          aria-label="Skip forward 10 seconds"
        >
          <SkipForward className="h-8 w-8 text-gray-300" />
        </Button>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between">
          <span className="text-sm text-gray-300">{formatTime(currentTime)}</span>
          <span className="text-sm text-gray-300">{formatTime(duration)}</span>
        </div>
        
        <Slider
          value={[sliderValue]}
          max={100}
          step={1}
          onValueChange={handleSliderChange}
          className="w-full"
        />
      </div>
    </div>
  );
};

export default AudioPlayer;
